import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Onboarding() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({
    name: "",
    birth_date: "",
    birth_time: "",
    birth_location: "",
    referred_by: "",
  });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/users/natal", form);
      setUser(res.data);
      toast.success("Your natal blueprint is calibrated ✨");
      nav("/reveal", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something misaligned");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-[#15111A]">
      <div className="starfield" />
      <div className="noise" />
      <div className="relative z-10 max-w-xl mx-auto px-6 sm:px-8 pt-16 pb-24">
        <p className="text-xs uppercase tracking-[0.3em] text-[#A89C98]">Step 1 · Natal Capture</p>
        <h1 className="font-heading text-4xl sm:text-5xl mt-3 tracking-tighter" data-testid="onboarding-title">Reveal your blueprint</h1>
        <p className="text-[#A89C98] mt-4">Four coordinates. Infinite trajectories.</p>

        <form onSubmit={onSubmit} className="mt-12 space-y-8" data-testid="onboarding-form">
          <FieldStack
            id="name" label="Full Name" testid="input-name"
            value={form.name} onChange={(v) => setForm({ ...form, name: v })}
            placeholder="e.g. Selene Voss"
          />
          <FieldStack
            id="birth_date" label="Birth Date" testid="input-birth-date"
            type="date"
            value={form.birth_date} onChange={(v) => setForm({ ...form, birth_date: v })}
          />
          <FieldStack
            id="birth_time" label="Exact Birth Time" testid="input-birth-time"
            type="time"
            value={form.birth_time} onChange={(v) => setForm({ ...form, birth_time: v })}
          />
          <FieldStack
            id="birth_location" label="Birth Location" testid="input-birth-location"
            value={form.birth_location} onChange={(v) => setForm({ ...form, birth_location: v })}
            placeholder="City, Country"
          />
          <FieldStack
            id="referred_by" label="Recommended by (optional)" testid="input-referred-by"
            value={form.referred_by} onChange={(v) => setForm({ ...form, referred_by: v })}
            placeholder="Person or business who sent you"
            required={false}
          />

          <Button data-testid="onboarding-submit" type="submit" disabled={busy} className="w-full h-12 bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none font-semibold uppercase tracking-widest">
            {busy ? "Aligning…" : "Reveal Blueprint"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function FieldStack({ id, label, testid, value, onChange, type = "text", placeholder, required = true }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.2em] text-[#A89C98]">{label}</Label>
      <Input
        id={id}
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-3 bg-transparent border-0 border-b border-[#A89C98]/30 rounded-none px-0 text-white focus-visible:ring-0 focus-visible:border-[#C8A96E] h-11 text-lg"
        style={{ colorScheme: "dark" }}
      />
    </div>
  );
}
